import { type FC } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import {
    IconStatsClose,
    IconSwipeUp,
    IconUpcomingLive
} from 'assets/svg/sprite'
import { ShowMore } from 'components/ShowMore/ShowMore'
import { ThreeDotsTrigger } from 'components-ref/atoms'
import { LastPostInfo } from 'components-ref/molecules'
import { PostInfoTop } from 'components-ref/molecules/post/PostInfoTop/PostInfoTop'
import { usePreviewContext } from 'context/previewContext'
import ImagePreview from 'features/Preview/ImagePreview'
import { MinimizeIcon } from 'features/Preview/Preview'
import PreviewActions from 'features/Preview/PreviewActions/PreviewActions'
import { useOrientation } from 'helpers/helperHooks'
import { extractMedia } from 'helpers/media'
import { renderPostText } from 'helpers/postHelpers'
import { formatedDate, someTimeAgo } from 'lib/dayjs'
import { type IPost } from 'types/interfaces/ITypes'

interface Props {
    post: IPost
    showControls: boolean
    isFirstPost: boolean
    hasNextPage: boolean
    minimizeFn: (_fileIndex: number, _type: string) => void
    toggleControls: () => void
    isLastPost?: boolean
    setIsOptionsNavOpen?: (val: boolean) => void
}

const SinglePostPreviewLive: FC<Props> = ({
    post,
    showControls,
    isFirstPost,
    minimizeFn,
    toggleControls,
    hasNextPage,
    isLastPost = false,
    setIsOptionsNavOpen
}) => {
    const deviceOrientation = useOrientation()
    const { t } = useTranslation()
    const {
        userId,
        query,
        minimized,
        setCommentsActive,
        clearModal
    } = usePreviewContext()

    const postFile = {
        userId: post.user_id,
        name: post.user?.display_name,
        text: post.body,
        mentions: post.tags,
        media: extractMedia(post),
        minimizedName: post.user?.display_name,
        minimizedAvatar:
            post.user?.cropped_avatar?.url ??
            post.user?.avatar?.url ??
            placeholderAvatar
    }

    if (!post.live || !deviceOrientation) return null
    return (
        <>
            <div
                className='preview__close'
                onClick={() => {
                    clearModal()
                }}
            >
                {minimized ? (
                    <IconStatsClose color='#A3A3A3' />
                ) : (
                    <IconStatsClose />
                )}
            </div>
            {showControls && (
                <ThreeDotsTrigger
                    onClick={(
                        e: React.MouseEvent<HTMLDivElement, MouseEvent>
                    ) => {
                        e.stopPropagation()
                        if (setIsOptionsNavOpen) {
                            setIsOptionsNavOpen(true)
                        }
                    }}
                />
            )}
            <div
                className={classNames(
                    'preview__file--wrapper',
                    minimized && 'minimized-video'
                )}
            >
                <MinimizeIcon
                    {...{ minimized, post, showControls }}
                    onClick={() => {
                        minimizeFn(0, 'video')
                    }}
                />
                {showControls && !minimized && (
                    <>
                        <ShowMore
                            userId={postFile.userId}
                            name={postFile.name}
                            time={someTimeAgo(post.created_at)}
                            tags={['#bowieblue', '#bluechowchow']}
                            text={postFile.text ? postFile.text : ''}
                            mentions={postFile.mentions}
                        />
                        <PreviewActions
                            stats={{
                                overallRevenue: post.overall_revenue,
                                purchasesCount: post.purchases_count,
                                purchasesSum: post.purchases_sum,
                                tipsCount: post.tips_count,
                                tipsSum: post.tips_sum,
                                conversionRate: post.conversion_rate,
                                uniqueImpressions: post.unique_impressions,
                                views: post.views,
                                uniqueViews: post.unique_views,
                                price: post.price
                            }}
                            modelData={{
                                postId: post.id,
                                modelId: post.user.id,
                                avatarSrc:
                                    post.user.avatar?.url ??
                                    post.user.cropped_avatar?.url ??
                                    placeholderAvatar
                            }}
                            query={query}
                            postId={post.id}
/*                             isMyPost={userId === post.user?.id}
 */                            isLiked={post.isLiked}
                            likes={post.likes_count}
                            comments={post.comment_count}
                            setCommentsActive={setCommentsActive}
                        />
                    </>
                )}
                <div
                    className={`preview__file ${post.live.img_orientation.toLowerCase()}`}
                >
                    <PostInfoTop
                        pinned={false}
                        live={!!post.live.active}
                        liveEnded={!!post.live.finished}
                        customClass='preview'
                    />
                    {minimized ? ( //
                        <div className='preview__minimized'>
                            <img
                                className='preview__file__image preview__file__image--minimized'
                                src={post.live.img_url}
                                alt={t('preview')}
                            />
                            <div className='preview__minimized__content'>
                                <div className='preview__minimized__content--top'>
                                    <div className='header__avatar'>
                                        <img
                                            src={postFile.minimizedAvatar}
                                            alt={t('placeholder')}
                                        />
                                    </div>
                                    <div className='header__name'>
                                        {postFile.minimizedName}
                                    </div>
                                </div>
                                <div className='preview__minimized__content--bottom'>
                                    <div className='content__text'>
                                        {renderPostText(
                                            postFile.text ?? '',
                                            postFile.mentions
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div onClick={toggleControls}>
                            <img
                                className='preview__background'
                                src={post.live.img_url}
                                alt={t('background')}
                            />
                            <ImagePreview src={post.live.img_url} />
                            {post.live_scheduled_at && (
                                <div className='post__files__file__upcoming'>
                                    <IconUpcomingLive />
                                    <p>{t('upcomingLiveStream')}</p>
                                    <div className='post__files__file__upcoming__date'>
                                        {formatedDate(
                                            post.live_scheduled_at,
                                            'MMM D, h:mm A'
                                        )}
                                    </div>
                                </div>
                            )}
{/*                             {shouldShowFirstTourGuide() && (
                                <div className='preview__swipe--icon'>
                                    <IconSwipeUp
                                        opacity={
                                            deviceOrientation.type.includes(
                                                'portrait'
                                            )
                                                ? post.live.img_orientation.toLowerCase() ===
                                                  'landscape'
                                                    ? 0.5
                                                    : 1
                                                : 1
                                        }
                                    />
                                    <p
                                        style={{
                                            opacity:
                                                deviceOrientation.type.includes(
                                                    'portrait'
                                                )
                                                    ? post.live.img_orientation.toLowerCase() ===
                                                      'landscape'
                                                        ? 0.5
                                                        : 1
                                                    : 1
                                        }}
                                    >
                                        {t('swipeUpForNextPost')}
                                    </p>
                                </div>
                            )} */}
                        </div>
                    )}
                    {isLastPost && !hasNextPage && <LastPostInfo />}
                </div>
            </div>
        </>
    )
}

export default SinglePostPreviewLive
